import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link2, UserPlus, Copy, Power, Trash2, Loader2, Gift, TrendingUp, Users, Briefcase, Repeat, Plus, ChevronDown, ChevronUp } from "lucide-react";

const money = (n) => `£${(n || 0).toLocaleString()}`;

export const ReferralEngine = () => {
  const [referrers, setReferrers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("member");
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const [r, s] = await Promise.all([api.getReferrers(), api.referralSummary()]);
    setReferrers(r); setSummary(s);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.addReferrer(name.trim(), type, email.trim());
      setName(""); setEmail("");
      await load();
      toast.success("Ambassador added — unique link issued.");
    } catch { toast.error("Couldn't add referrer."); }
    finally { setAdding(false); }
  };

  const s = summary;

  return (
    <div data-testid="referral-engine" className="mt-6">
      <div className="flex items-center gap-2 mb-4">
        <Gift size={18} className="text-emerald-400" />
        <h2 className="kz-head text-lg font-bold">Referral engine — members & sales partners</h2>
      </div>

      {/* Summary stats */}
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <StatCard icon={Users} label="Active referred members" value={s.active_referred} accent="text-emerald-400"
            sub={`${s.members} ambassadors · ${s.sales_partners} partners`} testid="stat-active-referred" />
          <StatCard icon={TrendingUp} label="Commission owed / mo" value={money(s.monthly_commission_owed)} accent="text-amber-400"
            sub={`${money(s.annual_commission_owed)} / year`} testid="stat-commission-owed" />
          <StatCard icon={Briefcase} label="Net member revenue / mo" value={money(s.net_monthly_from_referred)} accent="text-emerald-400"
            sub={`${money(s.gross_monthly_from_referred)} gross − commission`} testid="stat-net-revenue" />
          <StatCard icon={Repeat} label="Member reproduction rate" value={s.member_reproduction_rate} accent="text-blue-400"
            sub="avg members each ambassador makes" testid="stat-reproduction-rate" />
        </div>
      )}

      {/* Add referrer */}
      <div className="rounded-2xl border border-white/10 bg-[#121824] p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="referrer-name-input" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !adding && add()}
            placeholder="Name (member or sales partner)…"
            className="flex-1 bg-[#0B0F17] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40 transition-all" />
          <select data-testid="referrer-type-select" value={type} onChange={(e) => setType(e.target.value)}
            className="bg-[#0B0F17] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-200 outline-none focus:border-emerald-500/40">
            <option value="member">Member ambassador</option>
            <option value="sales_partner">Sales partner</option>
          </select>
          <input data-testid="referrer-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional)"
            className="sm:w-52 bg-[#0B0F17] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40 transition-all" />
          <button data-testid="add-referrer-btn" onClick={add} disabled={adding}
            className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all">
            {adding ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Add
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">£{s?.commission_per_member ?? 10}/mo per active referred member · one tier only · stops when a referrer leaves or a referred member churns.</p>
      </div>

      {/* Referrer list */}
      {referrers.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121824] p-8 text-center text-sm text-slate-400">
          No ambassadors yet. Add one, or mark a lead "joined" in the ledger — new members become ambassadors automatically.
        </div>
      ) : (
        <div className="space-y-2">
          {referrers.map((r) => <ReferrerRow key={r.id} r={r} onChange={load} />)}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#121824] p-4 kz-fade-up">
    <Icon size={15} className="text-slate-500" />
    <div className={`kz-head text-xl font-extrabold mt-2 ${accent}`}>{value}</div>
    <div className="text-[11px] text-slate-400 mt-0.5">{label}</div>
    {sub && <div className="text-[10px] mt-1 text-slate-500">{sub}</div>}
  </div>
);

const ReferrerRow = ({ r, onChange }) => {
  const [open, setOpen] = useState(false);
  const [refName, setRefName] = useState("");
  const [refEmail, setRefEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const link = `${window.location.origin}/join?ref=${r.code}`;

  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Referral link copied."); };

  const toggle = async () => {
    setBusy(true);
    try { await api.toggleReferrer(r.code); await onChange(); toast.message(r.active ? "Referrer deactivated — commissions paused." : "Referrer reactivated."); }
    finally { setBusy(false); }
  };
  const remove = async () => { setBusy(true); try { await api.deleteReferrer(r.id); await onChange(); toast.message("Removed."); } finally { setBusy(false); } };
  const addRef = async () => {
    if (!refName.trim()) return;
    setBusy(true);
    try { await api.addReferral(r.code, refName.trim(), refEmail.trim()); setRefName(""); setRefEmail(""); await onChange(); toast.success("Referred member recorded — £10/mo active."); }
    finally { setBusy(false); }
  };
  const toggleRef = async (id) => { setBusy(true); try { await api.toggleReferral(id); await onChange(); } finally { setBusy(false); } };

  return (
    <div data-testid={`referrer-${r.code}`} className={`rounded-2xl border p-4 transition-all ${r.active ? "border-white/10 bg-[#121824]" : "border-white/[0.06] bg-[#121824]/50 opacity-70"}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${r.type === "sales_partner" ? "bg-blue-500/15 text-blue-400" : "bg-emerald-500/15 text-emerald-400"}`}>
            {r.type === "sales_partner" ? <Briefcase size={15} /> : <Users size={15} />}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="kz-head font-bold text-slate-100 truncate">{r.name}</span>
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${r.type === "sales_partner" ? "bg-blue-500/15 text-blue-300" : "bg-emerald-500/15 text-emerald-300"}`}>{r.type === "sales_partner" ? "Partner" : "Member"}</span>
              {!r.active && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300">Paused</span>}
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <Link2 size={11} className="text-slate-500 shrink-0" />
              <span className="text-[11px] text-slate-500 kz-mono truncate max-w-[220px]">/join?ref={r.code}</span>
              <button data-testid={`copy-link-${r.code}`} onClick={copy} className="text-slate-500 hover:text-emerald-400 transition-colors"><Copy size={12} /></button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="kz-head text-lg font-extrabold text-emerald-400">{money(r.monthly_commission)}<span className="text-xs text-slate-500 font-normal">/mo</span></div>
            <div className="text-[10px] text-slate-500">{r.active_referrals} active · {r.total_referrals} total</div>
          </div>
          <button data-testid={`toggle-referrer-${r.code}`} onClick={toggle} disabled={busy} title={r.active ? "Deactivate" : "Reactivate"}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${r.active ? "bg-white/[0.06] text-slate-300 hover:bg-white/10" : "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"}`}>
            <Power size={14} />
          </button>
          <button onClick={() => setOpen(!open)} className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.06] text-slate-300 hover:bg-white/10 transition-all">
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-white/[0.06]">
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <input data-testid={`refer-name-${r.code}`} value={refName} onChange={(e) => setRefName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && addRef()}
              placeholder="Record a member they referred…"
              className="flex-1 bg-[#0B0F17] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500/40" />
            <input data-testid={`refer-email-${r.code}`} value={refEmail} onChange={(e) => setRefEmail(e.target.value)} placeholder="Email (optional)"
              className="sm:w-48 bg-[#0B0F17] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500/40" />
            <button data-testid={`add-referral-${r.code}`} onClick={addRef} disabled={busy}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.06] text-slate-100 font-semibold text-sm hover:bg-white/[0.1] disabled:opacity-60 transition-all">
              <Plus size={15} className="text-emerald-400" /> Referred
            </button>
          </div>
          {(r.referrals || []).length === 0 ? (
            <p className="text-xs text-slate-500">No referred members yet.</p>
          ) : (
            <div className="space-y-1.5">
              {r.referrals.map((x) => (
                <div key={x.id} data-testid={`referral-${x.id}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#0B0F17] border border-white/[0.04]">
                  <span className={`text-sm ${x.active ? "text-slate-200" : "text-slate-500 line-through"}`}>{x.referred_name}</span>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold ${x.active ? "text-emerald-400" : "text-slate-600"}`}>{x.active ? "£10/mo" : "churned"}</span>
                    <button data-testid={`toggle-referral-${x.id}`} onClick={() => toggleRef(x.id)} disabled={busy}
                      className="text-[10px] font-semibold px-2 py-1 rounded-md bg-white/[0.06] text-slate-300 hover:bg-white/10 transition-all">
                      {x.active ? "Mark churned" : "Reactivate"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button data-testid={`delete-referrer-${r.code}`} onClick={remove} disabled={busy}
            className="flex items-center gap-1.5 mt-3 text-xs text-red-400/80 hover:text-red-400 transition-colors">
            <Trash2 size={12} /> Remove referrer
          </button>
        </div>
      )}
    </div>
  );
};
